import { auth } from '../firebase/config';
import { getApiUrl } from '../utils/apiConfig';
import { fetchWithRetry, calcApiTimeout } from '../utils/fetchWithRetry';

const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) return { 'Content-Type': 'application/json' };
  try {
    const token = await user.getIdToken();
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

const apiRequest = async (url, options = {}) => {
  const authHeaders = await getAuthHeaders();
  const timeoutMs = calcApiTimeout('assessment-plan');
  const response = await fetchWithRetry(
    getApiUrl(url),
    { ...options, headers: { ...authHeaders, ...options.headers } },
    { maxRetries: 1, timeoutMs }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
};

export const detectSubjects = async (text, files) => {
  const token = await getAuthToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const fileArray = files ? (Array.isArray(files) ? files : [files]) : [];

  if (fileArray.length > 0) {
    const formData = new FormData();
    fileArray.forEach((f) => formData.append('files', f));
    const response = await fetch(getApiUrl('/api/assessment-plan/detect-subjects'), {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) return { multi: false, subjects: [] };
    return await response.json();
  }

  headers['Content-Type'] = 'application/json';
  const response = await fetch(getApiUrl('/api/assessment-plan/detect-subjects'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ text }),
  });
  if (!response.ok) return { multi: false, subjects: [] };
  return await response.json();
};

/**
 * selectedSubject: 문자열(1개) 또는 문자열 배열(여러 항목). 배열이면 selected_subjects 로 함께 보낸다.
 */
export const generateAssessmentPlan = async (previousPlanText, instructions, files, selectedSubject, planType = 'assessment', analysisMode = 'basic') => {
  const selectedList = Array.isArray(selectedSubject)
    ? selectedSubject.map((name) => String(name || '').trim()).filter(Boolean)
    : (selectedSubject ? [String(selectedSubject).trim()].filter(Boolean) : []);
  const singleSubject = selectedList.length === 1 ? selectedList[0] : null;
  const multiSubjects = selectedList.length > 1 ? selectedList : null;
  const token = await getAuthToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const fileArray = files ? (Array.isArray(files) ? files : [files]) : [];

  if (fileArray.length > 0) {
    const formData = new FormData();
    fileArray.forEach((f) => formData.append('files', f));
    if (instructions) formData.append('instructions', instructions);
    if (singleSubject) formData.append('selected_subject', singleSubject);
    if (multiSubjects) formData.append('selected_subjects', JSON.stringify(multiSubjects));
    formData.append('plan_type', planType);
    formData.append('analysis_mode', analysisMode);
    const timeoutMs = calcApiTimeout('assessment-plan');
    const response = await fetchWithRetry(
      getApiUrl('/api/assessment-plan/generate'),
      { method: 'POST', headers, body: formData },
      { maxRetries: 1, timeoutMs }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  headers['Content-Type'] = 'application/json';
  const timeoutMs = calcApiTimeout('assessment-plan');
  const response = await fetchWithRetry(
    getApiUrl('/api/assessment-plan/generate'),
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        previous_plan_text: previousPlanText,
        instructions,
        selected_subject: singleSubject,
        selected_subjects: multiSubjects,
        plan_type: planType,
        analysis_mode: analysisMode,
      }),
    },
    { maxRetries: 1, timeoutMs }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

export const applyAssessmentPlanToHwp = async ({
  sourceFileId,
  requestId,
  planText,
  planStructured,
  instructions,
  planType = 'assessment',
  webSearchSources = [],
}) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('한글 양식 결과를 만들려면 로그인이 필요해요.');
  }
  const response = await fetchWithRetry(
    getApiUrl('/api/assessment-plan/apply-hwp'),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        source_file_id: sourceFileId,
        request_id: requestId,
        plan_text: planText || '',
        plan_structured: planStructured || {},
        instructions: instructions || null,
        plan_type: planType,
        web_search_sources: Array.isArray(webSearchSources)
          ? webSearchSources.slice(0, 5)
          : [],
      }),
    },
    {
      maxRetries: 0,
      timeoutMs: 720_000,
      retryOnTimeout: false,
    }
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = typeof body?.detail === 'string'
      ? body.detail
      : body?.detail?.message;
    const error = new Error(
      detail || '한글 양식에 결과를 반영하지 못했어요.'
    );
    error.status = response.status;
    error.retryable = response.status === 409 || response.status >= 500;
    throw error;
  }
  return body;
};

export const generateAssessmentPlanHwp = async ({
  requestId,
  planText,
  planStructured,
  instructions,
  planType = 'assessment',
  webSearchSources = [],
}) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('한글 문서를 만들려면 로그인이 필요해요.');
  }
  const response = await fetchWithRetry(
    getApiUrl('/api/assessment-plan/generate-hwp'),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        request_id: requestId,
        plan_text: planText || '',
        plan_structured: planStructured || {},
        instructions: instructions || null,
        plan_type: planType,
        web_search_sources: Array.isArray(webSearchSources)
          ? webSearchSources.slice(0, 5)
          : [],
      }),
    },
    {
      maxRetries: 0,
      timeoutMs: 720_000,
      retryOnTimeout: false,
    }
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = typeof body?.detail === 'string'
      ? body.detail
      : body?.detail?.message;
    const error = new Error(
      detail || '한글 문서를 만들지 못했어요.'
    );
    error.status = response.status;
    error.retryable = response.status === 409 || response.status >= 500;
    throw error;
  }
  return body;
};

export const buildAssessmentPlanRefinePayload = (currentPlan, revisionInstruction, options = {}) => {
  const payload = {
    current_plan: currentPlan,
    revision_instruction: revisionInstruction,
  };
  if (options.targetItemIndex !== null && options.targetItemIndex !== undefined) {
    payload.target_item_index = options.targetItemIndex;
  }
  if (options.originalContext) {
    payload.original_context = options.originalContext;
  }
  if (options.revisionHistory && options.revisionHistory.length > 0) {
    payload.revision_history = options.revisionHistory;
  }
  if (options.planType) {
    payload.plan_type = options.planType;
  }
  if (options.sourcePlanId) {
    payload.source_plan_id = options.sourcePlanId;
  }
  return payload;
};

export const refineAssessmentPlan = async (currentPlan, revisionInstruction, options = {}) => {
  const payload = buildAssessmentPlanRefinePayload(
    currentPlan,
    revisionInstruction,
    options,
  );
  return await apiRequest('/api/assessment-plan/refine', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

// B-2 후속 2단계: 입력 칸의 [코드] 성취기준으로 평가계획서 초안(평문)을 만든다. 실패는 Error(detail).
export const draftPlanFromStandards = async ({ text, planType = 'assessment', schoolLevel } = {}) => {
  return apiRequest('/api/assessment-plan/draft-from-standards', {
    method: 'POST',
    body: JSON.stringify({
      text,
      plan_type: planType,
      ...(schoolLevel ? { school_level: schoolLevel } : {}),
    }),
  });
};

export const saveAssessmentPlan = async (data) => {
  return await apiRequest('/api/assessment-plan/save', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const getAssessmentPlanHistory = async (limit = 100, offset = 0, isPro = false) => {
  return await apiRequest(`/api/assessment-plan/history?limit=${limit}&offset=${offset}&is_pro=${isPro}`);
};

export const getAssessmentPlanRecord = async (recordId) => {
  return await apiRequest(`/api/assessment-plan/history/${recordId}`);
};

export const updateAssessmentPlanRecordMeta = async (recordId, data) => {
  return await apiRequest(`/api/assessment-plan/history/${recordId}/meta`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

export const deleteAssessmentPlanRecord = async (recordId) => {
  return await apiRequest(`/api/assessment-plan/history/${recordId}`, { method: 'DELETE' });
};
